import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { TenantService } from '../services/tenant.service';
import { Route, Router } from '@angular/router';
import { OverlayPanel } from 'primeng/overlaypanel';
import {
  AppConfigSettings,
  getActiveTenant$,
  PermissionTypes,
  userHasMultipleTenants,
  NotificationCounterService
} from '@arc/arc-shared-frontend';
import { MenuItem as AurMenuItem } from '@arc/aureus';
import { Store, select } from '@ngrx/store';
import { RoutingService } from '../services/routing.service';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'application-navigation-ui',
  templateUrl: './application-navigation.component.html',
  styleUrls: ['./application-navigation.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationNavigationComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() userName: string;
  @Input() tenantName: string;
  @Input() resellerId: string;

  @Output() onTenantSwitchClick = new EventEmitter();
  @Output() onLogoutClick = new EventEmitter();
  @Output() onProfileClick = new EventEmitter();

  @ViewChild('settingsOp') settingsOverlayPanel: OverlayPanel;
  @ViewChild('op') loggedInOverlayPanel: OverlayPanel;

  ngUnsubscribe = new Subject<void>();
  settingsMenu: MenuItem[];
  applicationsMenu: MenuItem[];
  settingsMenuItems: AurMenuItem[];
  loggedInMenu: AurMenuItem[];

  isGptVisible: boolean = false;
  isAnalyticsVisible: boolean = false;

  tooltipOptions = {
    showDelay: 0,
    tooltipPosition: 'bottom',
    escape: true // ARC-14900 -- Explicitly declare TRUE. For future readers, this is to prevent malicious input injection into the tooltip. The default is TRUE. Regardless, being explicit here for clarity.
  };

  notifications: number = 0;
  basePath!: string;

  constructor(
    private tenantService: TenantService,
    private router: Router,
    private permissionDefinition: PermissionTypes,
    private store: Store<any>,
    private routingService: RoutingService,
    private httpClient: HttpClient,
    private appSetting: AppConfigSettings,
    private notificationCounterService: NotificationCounterService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.notificationCounterService.notificationCount$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(count => {
        this.notifications = count;
        this.cdr.detectChanges();
      });
    const url = new URL(this.appSetting.getconfig('tenantServiceUrl')).origin;
    this.basePath = `${url}/gpt`;
    this.onCallCopilotNotifications();
  }

  onClickTabMenuItem() {
    this.onCallCopilotNotifications();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private closeMenuAfterNavigate(url: string) {
    this.router.navigate([url]);
    this.settingsOverlayPanel.hide();
  }

  ngOnChanges() {
    // During the pilot program, only show Finance Copilot (navitem) to those with the app enabled and permissioned to use it.
    this.permissionDefinition
      .hasPermissionAndApp$(
        this.store,
        'arc-permission-section-gpt-terminal:read',
        ['gpt-app']
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(val => (this.isGptVisible = val));

    this.permissionDefinition
      .hasPermissionAndApp$(
        this.store,
        'arc-permission-analytics-access:read',
        ['analytics-app']
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(permission => (this.isAnalyticsVisible = permission));

    const crossSellSuffix: string = '-mktg-contact-sales';

    this.applicationsMenu = [
      {
        label: 'Invoicing',
        id: 'invoicing',
        routerLink: [
          `/invoicing${
            !this.tenantService.hasApplication('invoicing')
              ? crossSellSuffix
              : ''
          }`
        ]
      },
      {
        label: 'Payments',
        id: 'bpn-app',
        routerLink: [
          `/payments${
            !this.tenantService.hasApplication('bpn-app') ? crossSellSuffix : ''
          }`
        ]
      },
      {
        label: 'Cash App',
        id: 'cash-app',
        routerLink: [
          `/cash-app${
            !this.tenantService.hasApplication('cash-app')
              ? crossSellSuffix
              : ''
          }`
        ]
      },
      {
        label: 'Collections',
        id: 'icontroller-collections-app',
        command: () => {
          const collectionsRoute: Route = {
            path: 'collections'
          };
          this.routingService.persistRoute('collections');
          this.tenantService.hasApplication('icontroller-collections-app')
            ? (location.href = this.routingService.collectionsWorkspaceV2URL)
            : this.router.navigate([
                `/collections${
                  !this.tenantService.hasApplication(
                    'icontroller-collections-app'
                  )
                    ? crossSellSuffix
                    : ''
                }`
              ]);
        }
      },
      {
        label: 'Credit',
        id: 'credit-app-2',
        routerLink: [
          `/credit-manager${
            !this.tenantService.hasApplication('credit-app-2')
              ? crossSellSuffix
              : ''
          }`
        ]
      },
      {
        label: 'Disputes',
        id: 'disputes',
        routerLink: [
          `/disputes${
            !this.tenantService.hasApplication('disputes-app')
              ? crossSellSuffix
              : ''
          }`
        ]
      }
    ];

    this.settingsMenu = [
      {
        icon: 'domain',
        id: 'companies-tab',
        routerLink: ['/tenants'],
        tooltip: 'ARC_COMPOSITE_MENU_TENANTS',
        visible: this.tenantService.canManageTenants
      },
      {
        icon: 'auto_awesome',
        id: 'gpt-terminal-tab ',
        routerLink: ['/gpt-terminal'],
        tooltip: 'ARC_COMPOSITE_MENU_GPT',
        visible: this.isGptVisible,
        hasNotificationIcon: true
      },
      {
        icon: 'timeline',
        id: 'analytics-tab',
        routerLink: ['/analytics'],
        tooltip: 'ARC_COMPOSITE_MENU_ANALYTICS',
        visible: this.isAnalyticsVisible
      },
      {
        icon: 'folder',
        id: 'accounts-tab',
        tooltip: 'ARC_COMPOSITE_MENU_ACCOUNTS',
        command: () => {
          const latestContext = this.routingService.retrieveRoute();
          if (
            latestContext === 'payments' &&
            this.tenantService.hasApplication('bpn-app')
          ) {
            this.router.navigate(['/payment-accounts']);
          } else if (latestContext === 'payments') {
            this.router.navigate(['/payments-mktg-contact-sales']);
          } else {
            this.router.navigate(['/accounts']);
          }
        }
      },
      {
        icon: 'settings',
        id: 'settings-tab',
        tooltip: 'ARC_COMPOSITE_MENU_SETTINGS',
        command: event => this.settingsOverlayPanel.toggle(event.originalEvent)
      },
      {
        icon: 'help_outline',
        id: 'help-tab',
        tooltip: 'ARC_COMPOSITE_HELPANDSUPPORT'
      }
    ];

    this.settingsMenuItems = [
      {
        label: 'ARC_COMPOSITE_MENU_USERS',
        icon: 'people',
        id: 'users-list-item',
        command: () => this.closeMenuAfterNavigate('/users'),
        visible$: this.permissionDefinition.isPermitted$(
          this.permissionDefinition.UserRoleManagement + ':read'
        )
      },
      {
        label: 'ARC_COMPOSITE_MENU_ROLES',
        icon: 'how_to_reg',
        id: 'roles-list-item',
        command: () => this.closeMenuAfterNavigate('/users/roles'),
        visible$: this.permissionDefinition.isPermitted$(
          this.permissionDefinition.UserRoleManagement + ':read'
        )
      },
      {
        label: 'ARC_COMPOSITE_SYSTEM',
        icon: 'settings_system_daydream',
        id: 'configuration-list-item',
        command: () => this.closeMenuAfterNavigate('/system')
      }
    ];

    this.loggedInMenu = [
      {
        label: 'ARC_COMPOSITE_MENU_PROFILE',
        icon: 'manage_accounts',
        id: 'profile-item',
        command: () => {
          this.clearActiveSettings();
          this.onProfileClick.emit();
          this.loggedInOverlayPanel.hide();
        }
      },
      {
        label: 'ARC_COMPOSITE_MENU_CHANGE_COMPANY',
        icon: 'swap_calls',
        id: 'change-company-item',
        command: () => {
          this.clearActiveSettings();
          this.onTenantSwitchClick.emit();
        },
        visible$: this.store.pipe(select(userHasMultipleTenants))
      },
      {
        label: 'ARC_COMPOSITE_MENU_LOGOUT',
        icon: 'power_settings_new',
        id: 'logout-item',
        command: () => {
          this.clearActiveSettings();
          this.onLogoutClick.emit();
        }
      }
    ];
  }

  public settingsActiveItem: MenuItem | undefined | null;
  clearActiveSettings = () => (this.settingsActiveItem = null);

  setActiveTab = (tabId: string) =>
    (this.settingsActiveItem = this.settingsMenu.find(
      (menuItem: MenuItem) => menuItem.id === tabId
    ));

  onSettingsOverlayHide() {
    switch (this.routingService.currentRoute) {
      case 'users':
      case 'profile':
      case 'system':
        break;
      case 'accounts':
        this.setActiveTab('accounts-tab');
        break;
      default:
        this.clearActiveSettings();
        break;
    }
  }

  public get currentRoute(): string {
    return this.routingService.currentRoute;
  }

  public get logoClass(): string {
    switch (this.resellerId?.toLowerCase()) {
      case 'jpmorgan':
        return 'logo-jpm';
      default:
        return 'logo-default';
    }
  }

  isProfileOverlayVisible: boolean = false;
  onProfileOverlayToggle = (overlayState: boolean) => {
    this.isProfileOverlayVisible = overlayState;

    if (overlayState && this.settingsActiveItem?.id === 'help-tab') {
      this.clearActiveSettings();
    }
  };

  onHomeClick = () =>
    this.router.navigate([this.routingService.retrieveRoute()]);

  onCallCopilotNotifications() {
    getActiveTenant$(this.store)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        switchMap(tenant =>
          this.httpClient.get<any>(
            `${this.basePath}/v2/tenants/${tenant.tenantId}/notifications`
          )
        )
      )
      .subscribe({
        next: v => {
          this.notifications =
            v.messages?.reduce(
              (count, message) => (message.read ? count : count + 1),
              0
            ) ?? 0;
          this.cdr.detectChanges();
        },
        error: err => {}
      });
  }
}
